/**
 * Guardrails: the steel beam on posts along a motorway's carriageways, and
 * along a main road where it bends sharply or stands high over the land
 * beside it. They are what keeps a car on the road: drawn with the bridge
 * structure, and walls to collide with (see `colliders.ts`).
 *
 * - **Motorways and their slip roads**: along both edges of every
 *   carriageway, so down the median too.
 * - **Arterials and collectors**: out of town (`rural`), along the outside of
 *   a curve tighter than `CURVE_RADIUS` for the class; anywhere, either side
 *   where the road stands more than `BANK_DROP` over the ground just beyond
 *   its edge.
 * - **Streets**, the same, but only out of town: in a town every corner is a
 *   tight curve, and a rail round a bend through a village fenced its houses
 *   off from the road.
 *
 * - **Every road on a bridge**, along both edges of the deck, on its edge
 *   beam (`bridges.ts`), run on from the rail beside the road onto it, or
 *   from nothing: a deck is never left open, however short.
 *
 * Never in a tunnel or an underpass's trough (their walls), nor where another
 * road meets the edge: a slip road, a side road, the carriageway alongside
 * where a divided road closes up, the deck alongside a deck; nor across a
 * path, a footway, a cycleway or steps that meet the road or cross it. There the rail
 * stops, and a stub shorter than `SHORTEST` off a bridge is left out.
 */

import type { RailingIndex } from './railings.js';
import { BRIDGE, TUNNEL } from './roads.js';

/** Out from the road's edge to the rail's face, metres; on a bridge, in from the deck's edge, on its edge beam. */
const SET_OUT = 0.5;
const ON_DECK = -0.2;
/** The beam: its bottom and top over the road, metres. */
const BEAM_BOTTOM = 0.45;
const BEAM_TOP = 0.8;
/** A trough's roof under the road over it, metres: as `UNDER_DECK` in `tunnelmesh.ts`. */
const ROOF_DEPTH = 1.0;
/** Between posts, metres, and a post's half thickness. */
const POST_SPACING = 4;
const POST_HALF = 0.06;
/** Tightest curve, radius in metres, a road of the class takes without a rail on its outside. */
const CURVE_RADIUS: Partial<Record<number, number>> = { 1: 250, 2: 120, 3: 60 };
/** Quads either side over which a curve's radius is taken: about 50 m in all. */
const CURVE_WINDOW = 2;
/** `ROAD_STYLE.service`: the least road with rails, on its bridges. */
const SERVICE = 4;
/** `ROAD_STYLE.street`: rails only out of town. */
const STREET = 3;
/** How far the road stands over the ground beyond its edge before it has a rail, metres. */
const BANK_DROP = 2.5;
/** How far beyond the edge another road's surface stops the rail, metres. */
const CLEAR_OF_ROADS = 1.6;
/** How near in height another road's surface beyond the edge must be to stop the rail, metres. */
const LEVEL_WITH = 1.5;
/** `ROAD_STYLE.steps`: the last of the ways that meet a road, no rail across them. */
const STEPS = 10;
/** How near the rail's line another way's end must come to meet the road there, metres past its half-width. */
const MEETS = 2;
/** Shortest piece of rail left standing, metres. */
const SHORTEST = 15;
/** Linear RGB: galvanised steel, the beam lighter than the posts. */
const BEAM = [0.42, 0.44, 0.46];
const POST = [0.3, 0.31, 0.32];

/** Road styles (`ROAD_STYLE`) with rails: motorway, arterial, collector. */
const MOTORWAY = 0;

export interface Guardrails {
  mesh: { positions: Float32Array; colors: Float32Array; indices: Uint32Array };
  /** x0, z0, x1, z1, bottom, top per piece, as `ColliderInput.walls`. */
  walls: Float32Array;
}

/**
 * The rails along a tile's roads. `positions` are the placed (clipped) road
 * ribbons, x, y, z per vertex, a left and right edge per cross-section;
 * `attributes` side, distance, style, half-width per vertex.
 */
export function buildGuardrails(
  positions: Float32Array, indices: Uint32Array, attributes: Float32Array, bridges: Uint8Array,
  underpasses: Uint8Array | undefined, ground: (x: number, z: number) => number,
  /** Whether (x, z) is out of town, where a street gets rails too. Everywhere, by default. */
  rural: (x: number, z: number) => boolean = () => true,
  /** OpenStreetMap's railings on bridges: a deck's edge with one has no rail of the game's. See `railings.ts`. */
  railings?: RailingIndex,
  /**
   * Whether (x, z) is over an underpass's trough, which a road over it runs on
   * over unbroken: a post there stands on the trough's roof under the road,
   * not down on its floor (`buildTroughs`).
   */
  overTrough: (x: number, z: number) => boolean = () => false,
  /** Whether (x, z) is on a bridge mapped as an area, whose own parapets stand at its sides (`bridgeareas.ts`). */
  onArea: (x: number, z: number) => boolean = () => false,
): Guardrails {
  const quads = Math.floor(indices.length / 6);
  const P = (v: number) => [positions[v * 3], positions[v * 3 + 1], positions[v * 3 + 2]];
  const style = (v: number) => Math.round(attributes[v * 4 + 2]);

  // Every road surface, by cell, for "is another road just beyond this edge".
  const CELL = 16;
  const cells = new Map<number, number[]>();
  const key = (cx: number, cz: number) => cx * 73856093 ^ cz * 19349663;
  for (let q = 0; q < quads; q++) {
    const a = indices[q * 6];
    if (style(a) > 4 || bridges[a] === TUNNEL) continue;
    const xs = [a, a + 1, a + 2, a + 3].map(v => positions[v * 3]), zs = [a, a + 1, a + 2, a + 3].map(v => positions[v * 3 + 2]);
    for (let cx = Math.floor(Math.min(...xs) / CELL); cx <= Math.floor(Math.max(...xs) / CELL); cx++) {
      for (let cz = Math.floor(Math.min(...zs) / CELL); cz <= Math.floor(Math.max(...zs) / CELL); cz++) {
        const k = key(cx, cz);
        let list = cells.get(k);
        if (list === undefined) cells.set(k, (list = []));
        list.push(q);
      }
    }
  }
  /** The quad's surface height at (x, z), if (x, z) is on it. */
  const onQuad = (q: number, x: number, z: number): number | undefined => {
    const a = indices[q * 6];
    // The quad's two triangles, as the ribbon is drawn: a, a+1, a+2 and a+1, a+3, a+2.
    for (const [i, j, k] of [[a, a + 1, a + 2], [a + 1, a + 3, a + 2]]) {
      const ax = positions[i * 3], az = positions[i * 3 + 2], bx = positions[j * 3], bz = positions[j * 3 + 2], cx = positions[k * 3], cz = positions[k * 3 + 2];
      const d = (bz - cz) * (ax - cx) + (cx - bx) * (az - cz);
      if (Math.abs(d) < 1e-9) continue;
      const u = ((bz - cz) * (x - cx) + (cx - bx) * (z - cz)) / d, v = ((cz - az) * (x - cx) + (ax - cx) * (z - cz)) / d;
      if (u >= -1e-3 && v >= -1e-3 && u + v <= 1 + 1e-3) return u * positions[i * 3 + 1] + v * positions[j * 3 + 1] + (1 - u - v) * positions[k * 3 + 1];
    }
    return undefined;
  };
  /**
   * Another road's surface at (x, z), not the run `own`'s, about level with
   * `y`: not a road passing under a deck, or over a road on a bridge, which
   * stopped the rail along a bridge over a motorway.
   */
  const otherRoad = (x: number, z: number, y: number, own: ReadonlySet<number>) => {
    for (const q of cells.get(key(Math.floor(x / CELL), Math.floor(z / CELL))) ?? []) {
      if (own.has(q)) continue;
      const h = onQuad(q, x, z);
      if (h !== undefined && Math.abs(h - y) < LEVEL_WITH) return true;
    }
    return false;
  };

  // Runs of quads, each section after the first shared with the quad before.
  const runs: number[][] = [];
  for (let q = 0; q < quads; q++) {
    const a = indices[q * 6];
    const last = runs[runs.length - 1];
    if (last !== undefined && indices[last[last.length - 1] * 6] + 2 === a) last.push(q);
    else runs.push([q]);
  }

  // Every way's centreline, any road, path, footway, cycleway, track or
  // steps, by cell: for "does a way meet the road here", which no rail may
  // cut across. With each run's ends, where a way stops at the road's edge.
  type Way = { q: number; x0: number; z0: number; y0: number; x1: number; z1: number; y1: number; hw: number; end0: boolean; end1: boolean };
  const wayCells = new Map<number, Way[]>();
  for (const run of runs) {
    run.forEach((q, i) => {
      const a = indices[q * 6], st = style(a);
      if (st > STEPS || bridges[a] === TUNNEL) return;
      const w: Way = {
        q,
        x0: (positions[a * 3] + positions[(a + 1) * 3]) / 2, z0: (positions[a * 3 + 2] + positions[(a + 1) * 3 + 2]) / 2, y0: (positions[a * 3 + 1] + positions[(a + 1) * 3 + 1]) / 2,
        x1: (positions[(a + 2) * 3] + positions[(a + 3) * 3]) / 2, z1: (positions[(a + 2) * 3 + 2] + positions[(a + 3) * 3 + 2]) / 2, y1: (positions[(a + 2) * 3 + 1] + positions[(a + 3) * 3 + 1]) / 2,
        hw: Math.hypot(positions[a * 3] - positions[(a + 1) * 3], positions[a * 3 + 2] - positions[(a + 1) * 3 + 2]) / 2,
        end0: i === 0, end1: i === run.length - 1,
      };
      for (let cx = Math.floor(Math.min(w.x0, w.x1) / CELL); cx <= Math.floor(Math.max(w.x0, w.x1) / CELL); cx++) {
        for (let cz = Math.floor(Math.min(w.z0, w.z1) / CELL); cz <= Math.floor(Math.max(w.z0, w.z1) / CELL); cz++) {
          const k = key(cx, cz);
          let list = wayCells.get(k);
          if (list === undefined) wayCells.set(k, (list = []));
          list.push(w);
        }
      }
    });
  }
  /**
   * Whether another way meets the road along the rail from `a` to `b` (x, y,
   * z): crosses its line, or ends within its width and `MEETS` of it, about
   * level with it. A path running alongside does not; one passing under a
   * bridge does not either.
   */
  const meets = (a: number[], b: number[], own: ReadonlySet<number>, ownEnds: ReadonlyArray<readonly [number, number]>) => {
    const seen = new Set<Way>();
    const ex = b[0] - a[0], ez = b[2] - a[2], l2 = ex * ex + ez * ez;
    const distTo = (x: number, z: number) => {
      const t = l2 > 0 ? Math.max(0, Math.min(1, ((x - a[0]) * ex + (z - a[2]) * ez) / l2)) : 0;
      return { d: Math.hypot(x - a[0] - ex * t, z - a[2] - ez * t), y: a[1] + (b[1] - a[1]) * t };
    };
    for (let cx = Math.floor((Math.min(a[0], b[0]) - MEETS) / CELL); cx <= Math.floor((Math.max(a[0], b[0]) + MEETS) / CELL); cx++) {
      for (let cz = Math.floor((Math.min(a[2], b[2]) - MEETS) / CELL); cz <= Math.floor((Math.max(a[2], b[2]) + MEETS) / CELL); cz++) {
        for (const w of wayCells.get(key(cx, cz)) ?? []) {
          if (seen.has(w) || own.has(w.q)) continue;
          seen.add(w);
          // Its line across the rail's.
          const fx = w.x1 - w.x0, fz = w.z1 - w.z0, den = ex * fz - ez * fx;
          if (Math.abs(den) > 1e-9) {
            const t = ((w.x0 - a[0]) * fz - (w.z0 - a[2]) * fx) / den, u = ((w.x0 - a[0]) * ez - (w.z0 - a[2]) * ex) / den;
            if (t >= 0 && t <= 1 && u >= 0 && u <= 1 && Math.abs(w.y0 + (w.y1 - w.y0) * u - (a[1] + (b[1] - a[1]) * t)) < LEVEL_WITH) return true;
          }
          // Or its end at the road, short of the rail's line.
          for (const [is, x, z, y] of [[w.end0, w.x0, w.z0, w.y0], [w.end1, w.x1, w.z1, w.y1]] as const) {
            if (!is) continue;
            // Not the next way of this road, where the map split it.
            if (ownEnds.some(([ox, oz]) => Math.hypot(ox - x, oz - z) < 2)) continue;
            const near = distTo(x, z);
            if (near.d < w.hw + MEETS && Math.abs(near.y - y) < LEVEL_WITH) return true;
          }
        }
      }
    }
    return false;
  };

  const mesh = { positions: [] as number[], colors: [] as number[], indices: [] as number[] };
  const quad = (a: number[], b: number[], c: number[], d: number[], col: number[]) => {
    const base = mesh.positions.length / 3;
    mesh.positions.push(...a, ...b, ...c, ...d);
    for (let k = 0; k < 4; k++) mesh.colors.push(col[0], col[1], col[2]);
    mesh.indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  };
  const walls: number[] = [];

  for (const run of runs) {
    const s = style(indices[run[0] * 6]);
    // Roads only; a lesser road than a street has them only on its bridges.
    if (s > SERVICE) continue;
    const own = new Set(run);
    const firstA = indices[run[0] * 6], lastA = indices[run[run.length - 1] * 6] + 2;
    const ownEnds: Array<[number, number]> = [firstA, lastA].map(v =>
      [(positions[v * 3] + positions[(v + 1) * 3]) / 2, (positions[v * 3 + 2] + positions[(v + 1) * 3 + 2]) / 2]);
    // The curve at each quad, over a window either side: the map's line is
    // a few points, so quad by quad the radius jumps between tight and
    // straight, and a rail round the bend came out in stubs.
    const heading = run.map(q => {
      const a = indices[q * 6];
      return Math.atan2((positions[(a + 2) * 3 + 2] + positions[(a + 3) * 3 + 2] - positions[a * 3 + 2] - positions[(a + 1) * 3 + 2]) / 2,
        (positions[(a + 2) * 3] + positions[(a + 3) * 3] - positions[a * 3] - positions[(a + 1) * 3]) / 2);
    });
    const along = run.map(q => {
      const a = indices[q * 6];
      return Math.hypot((positions[(a + 2) * 3] + positions[(a + 3) * 3] - positions[a * 3] - positions[(a + 1) * 3]) / 2,
        (positions[(a + 2) * 3 + 2] + positions[(a + 3) * 3 + 2] - positions[a * 3 + 2] - positions[(a + 1) * 3 + 2]) / 2);
    });
    /** Radius of the curve round quad i, metres, and which side is its outside (0 left, 1 right). */
    const curve = (i: number): { radius: number; outside: 0 | 1 } => {
      const i0 = Math.max(0, i - CURVE_WINDOW), i1 = Math.min(run.length - 1, i + CURVE_WINDOW);
      let turn = 0, arc = 0;
      for (let k = i0; k <= i1; k++) {
        arc += along[k];
        if (k > i0) { let d = heading[k] - heading[k - 1]; d -= Math.round(d / (2 * Math.PI)) * 2 * Math.PI; turn += d; }
      }
      // Turning toward +angle (from x round to z) the right edge, vertex a + 1, is outside.
      return { radius: Math.abs(turn) > 1e-6 ? arc / Math.abs(turn) : Infinity, outside: turn > 0 ? 1 : 0 };
    };
    for (const side of [0, 1] as const) {
      // Per quad: does this edge have a rail, and where does it run.
      const pieces: Array<{ rail: boolean; from: number[]; to: number[]; out: [number, number]; deck: boolean }> = run.map((q, qi) => {
        const a = indices[q * 6];
        const edge0 = P(a + side), edge1 = P(a + 2 + side), other0 = P(a + 1 - side), other1 = P(a + 3 - side);
        // Out from the road: from the other edge across to this one.
        let ox = (edge0[0] + edge1[0] - other0[0] - other1[0]) / 2, oz = (edge0[2] + edge1[2] - other0[2] - other1[2]) / 2;
        const ol = Math.hypot(ox, oz) || 1;
        ox /= ol; oz /= ol;
        // On a deck at either end, the rail on its edge beam; eased out to
        // beside the road over the quad running onto it.
        const deck0 = bridges[a] === BRIDGE, deck1 = bridges[a + 2] === BRIDGE, deck = deck0 && deck1;
        const set0 = deck0 ? ON_DECK : SET_OUT, set1 = deck1 ? ON_DECK : SET_OUT;
        const from = [edge0[0] + ox * set0, edge0[1], edge0[2] + oz * set0];
        const to = [edge1[0] + ox * set1, edge1[1], edge1[2] + oz * set1];
        const outOfTown = rural((edge0[0] + edge1[0]) / 2, (edge0[2] + edge1[2]) / 2);
        if (bridges[a] === TUNNEL || bridges[a + 2] === TUNNEL || underpasses?.[a] === 1) return { rail: false, from, to, out: [ox, oz], deck };
        if ((deck0 || deck1) && onArea((edge0[0] + edge1[0]) / 2, (edge0[2] + edge1[2]) / 2)) return { rail: false, from, to, out: [ox, oz], deck };
        if (deck0 || deck1) {
          // Not where the deck alongside runs on from this one's edge, nor
          // where a mapped railing stands along it.
          let rail = railings === undefined ||
            railings.outward((edge0[0] + edge1[0]) / 2, (edge0[2] + edge1[2]) / 2, ox, oz) === undefined;
          for (const t of [0.25, 0.5, 0.75]) {
            const ex = edge0[0] + (edge1[0] - edge0[0]) * t, ez = edge0[2] + (edge1[2] - edge0[2]) * t;
            if (otherRoad(ex + ox * 0.6, ez + oz * 0.6, edge0[1] + (edge1[1] - edge0[1]) * t, own)) { rail = false; break; }
          }
          if (rail && meets(from, to, own, ownEnds)) rail = false;
          return { rail, from, to, out: [ox, oz], deck: true };
        }
        if ((s === STREET && !outOfTown) || (s !== MOTORWAY && CURVE_RADIUS[s] === undefined)) return { rail: false, from, to, out: [ox, oz], deck };
        let rail = s === MOTORWAY;
        if (!rail) {
          // The outside of a sharp curve.
          const c = curve(qi);
          rail = outOfTown && c.outside === side && c.radius < CURVE_RADIUS[s]!;
        }
        if (!rail) {
          // High over the land beyond the edge.
          for (const t of [0, 0.5, 1]) {
            const x = edge0[0] + (edge1[0] - edge0[0]) * t + ox * 3, z = edge0[2] + (edge1[2] - edge0[2]) * t + oz * 3;
            if (edge0[1] + (edge1[1] - edge0[1]) * t - ground(x, z) > BANK_DROP) { rail = true; break; }
          }
        }
        // Not where another road's surface lies just beyond the edge.
        if (rail) {
          for (const t of [0, 0.25, 0.5, 0.75, 1]) {
            const ex = edge0[0] + (edge1[0] - edge0[0]) * t, ez = edge0[2] + (edge1[2] - edge0[2]) * t;
            const ey = edge0[1] + (edge1[1] - edge0[1]) * t;
            if (otherRoad(ex + ox * 0.3, ez + oz * 0.3, ey, own) || otherRoad(ex + ox * CLEAR_OF_ROADS, ez + oz * CLEAR_OF_ROADS, ey, own)) { rail = false; break; }
          }
          // Nor across a path, a footway or steps meeting the road, or a way crossing it.
          if (rail && meets(from, to, own, ownEnds)) rail = false;
        }
        return { rail, from, to, out: [ox, oz], deck };
      });
      // Into pieces of rail; short ones left out.
      for (let i = 0; i < pieces.length;) {
        if (!pieces[i].rail) { i++; continue; }
        let j = i, length = 0, onDeck = false;
        while (j < pieces.length && pieces[j].rail) {
          length += Math.hypot(pieces[j].to[0] - pieces[j].from[0], pieces[j].to[2] - pieces[j].from[2]);
          onDeck ||= pieces[j].deck;
          j++;
        }
        if (length >= SHORTEST || onDeck) {
          let travelled = 0, nextPost = 0;
          for (let k = i; k < j; k++) {
            const { from, to, out, deck } = pieces[k];
            const len = Math.hypot(to[0] - from[0], to[2] - from[2]);
            if (len < 1e-3) continue;
            const lo = Math.min(from[1], to[1]);
            walls.push(from[0], from[2], to[0], to[2], lo - 0.5, Math.max(from[1], to[1]) + BEAM_TOP + 0.05);
            // The beam: its face to the road, its back, its top.
            const at = (p: number[], h: number, back = 0) => [p[0] + out[0] * back, p[1] + h, p[2] + out[1] * back];
            quad(at(from, BEAM_BOTTOM), at(to, BEAM_BOTTOM), at(to, BEAM_TOP), at(from, BEAM_TOP), BEAM);
            quad(at(to, BEAM_BOTTOM, 0.08), at(from, BEAM_BOTTOM, 0.08), at(from, BEAM_TOP, 0.08), at(to, BEAM_TOP, 0.08), BEAM);
            quad(at(from, BEAM_TOP), at(to, BEAM_TOP), at(to, BEAM_TOP, 0.08), at(from, BEAM_TOP, 0.08), BEAM);
            // Posts, behind the beam, down to the ground.
            while (nextPost <= travelled + len) {
              const t = (nextPost - travelled) / len;
              const px = from[0] + (to[0] - from[0]) * t + out[0] * 0.14, pz = from[2] + (to[2] - from[2]) * t + out[1] * 0.14;
              const top = from[1] + (to[1] - from[1]) * t + BEAM_TOP - 0.05;
              // On a deck, from the deck, and on a trough's roof from the
              // roof; else down into the ground.
              const foot = deck ? top - BEAM_TOP : overTrough(px, pz) ? top - BEAM_TOP - ROOF_DEPTH
                : Math.min(top - BEAM_TOP, ground(px, pz)) - 0.3;
              const ax = (to[0] - from[0]) / len * POST_HALF, az = (to[2] - from[2]) / len * POST_HALF;
              const bx = out[0] * POST_HALF, bz = out[1] * POST_HALF;
              const c = [[px - ax - bx, pz - az - bz], [px + ax - bx, pz + az - bz], [px + ax + bx, pz + az + bz], [px - ax + bx, pz - az + bz]];
              for (let e = 0; e < 4; e++) {
                const [x0, z0] = c[e], [x1, z1] = c[(e + 1) % 4];
                quad([x0, foot, z0], [x1, foot, z1], [x1, top, z1], [x0, top, z0], POST);
              }
              nextPost += POST_SPACING;
            }
            travelled += len;
          }
        }
        i = j;
      }
    }
  }
  return {
    mesh: { positions: new Float32Array(mesh.positions), colors: new Float32Array(mesh.colors), indices: new Uint32Array(mesh.indices) },
    walls: new Float32Array(walls),
  };
}

